/**
 * Brand and semantic color tokens for Afrilink.
 * Authoritative spec: _bmad-output/AFRILINK_DESIGN_SYSTEM.md §1.
 *
 * Use these tokens (or Tailwind utilities derived from them) instead of hard-coded hex values
 * anywhere in marketing/ or portal/.
 */

export const brandColors = {
  // Brand palette. ADOPTED 2026-11-05 from images/Group_img/Logo/SightHost-Brand-Pack
  // (owner decision). The pack is the source; these values are not picked.
  // README states orange #F58C1E, teal #276E84, dark #212121. The artwork's
  // pixels are #F48B1E, #276E84, #212121, a difference nobody can see.
  // check-brand-artwork.mjs fails CI if this file leaves the artwork.
  //
  // Text on orange is ink, 6.63:1. White on orange is 2.43:1 and is never used.
  // Orange is a fill: never text on a light ground. Teal and orange never carry
  // text on each other (2.37:1). Teal on #212121 is 2.80:1, hence secondaryOnDark.
  primary: '#F58C1E',          // Brand orange: primary CTAs, accents
  secondary: '#276E84',        // Brand teal: secondary actions, links on light
  secondaryHover: '#215E71',   // Derived: 7.23:1 under white text
  secondaryOnDark: '#4AA3BE',  // Derived: teal as TEXT on dark, 5.04:1 on #292929
  accent: '#FFB347',           // Light orange tint, gradient end (not in the pack)

  // Neutral dark. Replaces navy, retired 2026-11-05: the pack has no navy.
  // 16.10:1 on white, 6.63:1 on the brand orange.
  ink: {
    DEFAULT: '#212121',  // Headings, body emphasis, text on orange
    soft: '#424242',
    muted: '#616161',
  },

  // Trust / info
  //
  // `dark` on each semantic colour exists for TEXT. The DEFAULT shades were
  // chosen as fills and are too light to set type in: on a white card they
  // measure 3.24:1 (success), 3.39:1 (warning), 4.03:1 (trust) and 4.11:1
  // (danger), and StatusBadge sets them at 12px semibold — well under the
  // 18.66px bold that would let 3:1 pass. Nine of the ten badge tones failed
  // AA, on 69 files' worth of "paid", "overdue" and "failed" labels.
  //
  // Each `dark` clears 4.5:1 against its own 10%-tint background (which is what
  // the badge actually renders on, and is very slightly harder than plain
  // white). Use DEFAULT for fills, borders, dots and icons; use `dark` whenever
  // the colour carries words. Re-measure if you change either.
  trust: {
    DEFAULT: '#3182ce',
    light: '#63b3ed',
    dark: '#2470bd',   // 4.52:1 on trust/10
  },

  // Status
  success: {
    DEFAULT: '#38a169',
    light: '#68d391',
    dark: '#2b7a50',   // 4.81:1 on success/10
  },
  // Yellow-amber since 2026-11-05: the old #dd6b20 sat beside the brand orange,
  // so a warning read as a brand accent.
  warning: {
    DEFAULT: '#CA8A04',
    light: '#EAB308',
    dark: '#8A5A00',   // 5.37:1 on warning/10
  },
  danger: {
    DEFAULT: '#e53e3e',
    light: '#fc8181',
    dark: '#cc2f2f',   // 4.56:1 on danger/10
  },
} as const;

/**
 * Per-app theme accents — top-bar-only layout (no sidebar).
 * `topBarAccent` is rendered as a 2px bottom border under the top bar so
 * each app has a subtle identity strip without a coloured sidebar.
 * Keep in sync with ARCHITECTURE_FRAMEWORK.md §5.1.
 */
export const appThemes = {
  client: {
    topBarAccent: brandColors.primary,    // orange strip — customer-facing
    primary: brandColors.primary,
    label: 'Client portal',
  },
  admin: {
    topBarAccent: brandColors.secondary,  // teal strip — staff
    primary: brandColors.secondary,
    label: 'Admin',
  },
  partner: {
    topBarAccent: brandColors.secondaryOnDark, // light teal strip — resellers
    primary: brandColors.secondary,
    label: 'Partner',
  },
} as const;

export const brandGradients = {
  primary: `linear-gradient(135deg, ${brandColors.primary} 0%, ${brandColors.accent} 100%)`,
  secondary: `linear-gradient(135deg, ${brandColors.secondaryHover} 0%, ${brandColors.secondary} 100%)`,
} as const;

export type BrandColor = keyof typeof brandColors;
export type AppTheme = keyof typeof appThemes;
